import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Spinner, useToast, InlineSpinner } from '../../components/UI';
import { SectionIcon } from '../../components/StudentUI';
import { fmtDate, titleCase } from '../../utils/format';
import ProfilePicture from '../../components/ProfilePicture';
import { useAuth } from '../../context/AuthContext';

const ReadOnlyNote = ({ children }) => (
  <div className="stu-readonly-note">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
    </svg>
    {children}
  </div>
);

const FIELDS = [
  ['student_no',        'Student No.'],
  ['name',              'Full name'],
  ['email',             'Email'],
  ['phone',             'Phone'],
  ['intake',            'Intake'],
  ['enrollment_date',   'Enrolment date'],
  ['language',          'Language'],
  ['guiding_language',  'Guiding language'],
];

const PW_FIELDS = [
  ['current_password', 'Current password', 'current-password'],
  ['new_password',      'New password',      'new-password'],
  ['confirm_password',  'Confirm new password', 'new-password'],
];

function ChangePasswordCard() {
  const toast = useToast();
  const [form, setForm]       = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving]   = useState(false);
  const [visible, setVisible] = useState({ current_password: false, new_password: false, confirm_password: false });

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleVisible = key => setVisible(v => ({ ...v, [key]: !v[key] }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.current_password || !form.new_password || !form.confirm_password) {
      toast('Please fill in all password fields.', 'error');
      return;
    }
    if (form.new_password.length < 8) {
      toast('New password must be at least 8 characters.', 'error');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast('New password and confirmation do not match.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put('/me/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast('Password changed successfully.', 'success');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to change password.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stu-card">
      <div className="stu-card-head">
        <h3 className="d-flex align-items-center"><SectionIcon icon="bi-shield-lock" color="var(--sms-stat-red)" />Change password</h3>
      </div>
      <div className="stu-card-body">
        <form onSubmit={handleSubmit} noValidate style={{ maxWidth: 360 }}>
          {PW_FIELDS.map(([key, label, autoComplete]) => (
            <div key={key} className="mb-3">
              <label htmlFor={`stu-${key}`} className="stu-field-label" style={{ display: 'block', marginBottom: 6 }}>
                {label}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id={`stu-${key}`}
                  type={visible[key] ? 'text' : 'password'}
                  autoComplete={autoComplete}
                  className="sms-form-control"
                  style={{ paddingRight: 40 }}
                  value={form[key]}
                  onChange={e => setField(key, e.target.value)}
                  onPaste={key === 'confirm_password' ? e => e.preventDefault() : undefined}
                  disabled={saving}
                />
                <button
                  type="button"
                  onClick={() => toggleVisible(key)}
                  aria-label={visible[key] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', padding: 4, cursor: 'pointer',
                    color: 'var(--sms-txt2)', display: 'flex', alignItems: 'center', lineHeight: 1,
                  }}
                >
                  <i className={`bi ${visible[key] ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
          <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
            {saving
              ? <><InlineSpinner /> Saving…</>
              : <><i className="bi bi-key-fill" aria-hidden="true" /> Update password</>
            }
          </button>
        </form>
      </div>
    </div>
  );
}

export default function StudentProfilePage() {
  const { patchUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const loadProfile = () => api.get('/me/profile').then(res => {
    setProfile(res.data.data);
    patchUser({ profile_picture: !!res.data.data?.profile_picture });
  });

  useEffect(() => {
    let cancelled = false;
    api.get('/me/profile').then(res => {
      if (cancelled) return;
      setProfile(res.data.data);
      patchUser({ profile_picture: !!res.data.data?.profile_picture });
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="stu-page"><Spinner /></div>;

  const fmt = (key, val) => {
    if (!val) return '—';
    if (key === 'enrollment_date') return fmtDate(val);
    if (key === 'name') return titleCase(val);
    if (key === 'email') return String(val).toLowerCase();
    return val;
  };

  return (
    <>
      <div className="stu-topbar">
        <div className="stu-topbar-title">My Profile</div>
        <div className="stu-topbar-sub">Your student record</div>
      </div>
      <div className="stu-page">
        <ReadOnlyNote>Contact your administrator to update these details.</ReadOnlyNote>

        <div className="stu-card">
          <div className="stu-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-person-vcard" color="var(--sms-stat-blue)" />My profile</h3>
            {profile?.status && <span className="stu-pill ok">{titleCase(profile.status)}</span>}
          </div>
          <div className="stu-card-body">
            <div style={{ marginBottom: 20 }}>
              <ProfilePicture
                editable hasPicture={!!profile?.profile_picture}
                getUrl={`/students/${profile?.student_no}/picture`}
                uploadUrl="/me/picture" deleteUrl="/me/picture"
                onChange={loadProfile}
              />
            </div>
            <div className="stu-profile-grid">
              {FIELDS.map(([key, label]) => (
                <div key={key}>
                  <div className="stu-field-label">{label}</div>
                  <div className="stu-field-value">{fmt(key, profile?.[key])}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <ChangePasswordCard />
      </div>
    </>
  );
}
